import {
  BseStateNameDto,
  BseStatusDto,
  EventKindDto,
  PublicEventStatusDto,
  type EventListItemDto,
  type ListCombinedEventsForBuyerApiArg,
} from "../../autogen/bff-api";
import type { EventStatusInput } from "./EventStatusTag";

type EventStatusFilter = NonNullable<ListCombinedEventsForBuyerApiArg["statuses"]>;

// Wound up: awarded, cancelled or otherwise finished. Nothing is expected of the buyer any more.
const TERMINAL_V2_STATUSES: PublicEventStatusDto[] = [
  PublicEventStatusDto.Awarded,
  PublicEventStatusDto.Cancelled,
  PublicEventStatusDto.Closed,
  PublicEventStatusDto.Completed,
];

// BSE `Completed` is not terminal — it means the bidding window closed and evaluation is under way,
// mirroring V2's EVALUATION.
const TERMINAL_V1_STATUSES: BseStateNameDto[] = [
  BseStateNameDto.Awarded,
  BseStateNameDto.Cancelled,
  BseStateNameDto.Closed,
];

/**
 * Active = neither a draft nor finished, across both generations. Derived by subtraction rather than
 * listed, so a status added to the API later shows up on the home instead of silently vanishing from
 * it — the same rule the BFF applies in `SourcingEventStatus.isActive`.
 *
 * PUBLICATION_PENDING and PUBLICATION_FAILED land here: the buyer has committed to announcing, and a
 * failed Doffin publish needs attention rather than sitting quietly among the drafts.
 */
export const ACTIVE_EVENT_STATUSES: EventStatusFilter = [
  ...Object.values(PublicEventStatusDto).filter(
    (status) => status !== PublicEventStatusDto.Draft && !TERMINAL_V2_STATUSES.includes(status)
  ),
  ...Object.values(BseStateNameDto).filter(
    (status) => status !== BseStateNameDto.Draft && !TERMINAL_V1_STATUSES.includes(status)
  ),
];

export const DRAFT_EVENT_STATUSES: EventStatusFilter = [PublicEventStatusDto.Draft, BseStateNameDto.Draft];

export const TERMINAL_EVENT_STATUSES: EventStatusFilter = [...TERMINAL_V2_STATUSES, ...TERMINAL_V1_STATUSES];

export const eventStatusTagInput = (event: EventListItemDto): EventStatusInput | null => {
  if (event.kind === EventKindDto.V1 && event.bseStatus) return { kind: "BSE", status: event.bseStatus };
  if (event.kind === EventKindDto.V2 && event.publicStatus) return { kind: "PUBLIC_V2", status: event.publicStatus };
  return null;
};

export const listItemIsDraft = (event: EventListItemDto): boolean =>
  event.kind === EventKindDto.V1
    ? event.bseStatus === BseStatusDto.Draft
    : event.publicStatus === PublicEventStatusDto.Draft;

export const listItemIsAwarded = (event: EventListItemDto): boolean =>
  event.kind === EventKindDto.V1
    ? event.bseStatus === BseStatusDto.Awarded
    : event.publicStatus === PublicEventStatusDto.Awarded;

const SOON_DAYS = 3;
export const isDeadlineSoon = (deadline: string | null | undefined): boolean => {
  if (!deadline) return false;
  const ms = new Date(deadline).getTime() - Date.now();
  return ms >= 0 && ms <= SOON_DAYS * 86400000;
};
